/**
 * The Approval Center. ARCHITECTURE.md section 8.
 *
 * APPROVE · REJECT · MODIFY · COMMENT, and always SEE SUPPORTING DATA.
 *
 * Two things this screen is careful about:
 *
 *  1. **It shows the reasoning, not the model's thinking.** The recommendation is a conclusion
 *     plus concrete reasons plus the data it came from (CLAUDE.md 3.9). Chain-of-thought is not
 *     stored and cannot be displayed.
 *  2. **It never claims an action ran.** Approving records a decision; whether the action executed
 *     is a separate fact, and the result says which happened. An approval that reported success
 *     after failing to run is how something gets paid for twice.
 */

import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type ApprovalDetail, type ApprovalSummary } from '../api.js';

const RISK_TONE: Record<string, string> = {
  LOW: '#15803d',
  MEDIUM: '#b45309',
  HIGH: '#b91c1c',
};

export function ApprovalsPage(): ReactNode {
  const [status, setStatus] = useState<'pending' | 'all'>('pending');
  // The open approval lives in the URL as `?id=`, not in component state: a link from elsewhere —
  // the Bookkeeping queue's "Open in Approvals to modify" — lands on ONE approval rather than the
  // list, a reload keeps the drawer open, and the address bar is a link to what the owner is
  // looking at. Replaced, not pushed, so Back leaves the page rather than walking through every
  // drawer that was opened. Added 2026-09-23 (ROADMAP §7).
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.get('id');
  const setSelected = (id: string | null): void => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (id) next.set('id', id);
        else next.delete('id');
        return next;
      },
      { replace: true },
    );
  };

  const list = useQuery({
    queryKey: ['approvals', status],
    queryFn: () => api.get<{ approvals: ApprovalSummary[] }>(`/api/approvals?status=${status}`),
    refetchInterval: 15_000,
  });

  const approvals = list.data?.approvals ?? [];

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Approvals</h1>
          <p className="subtitle">Nothing medium- or high-risk executes without a decision here.</p>
        </div>
        <div className="tabs">
          <button
            type="button"
            className={status === 'pending' ? 'tab active' : 'tab'}
            onClick={() => setStatus('pending')}
          >
            Waiting on you
          </button>
          <button
            type="button"
            className={status === 'all' ? 'tab active' : 'tab'}
            onClick={() => setStatus('all')}
          >
            All
          </button>
        </div>
      </header>

      {list.isLoading && <p className="muted">Loading…</p>}
      {list.isError && <p className="error">Could not load approvals.</p>}

      {list.data && approvals.length === 0 && (
        <div className="card empty">
          <h2>{status === 'pending' ? 'Nothing needs your decision.' : 'No approvals yet.'}</h2>
          <p>
            {status === 'pending'
              ? 'When an agent proposes something medium- or high-risk, it arrives here.'
              : 'No agent has proposed an action yet.'}
          </p>
        </div>
      )}

      {approvals.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>What</th>
              <th>Agent</th>
              <th>Risk</th>
              <th>Requested</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {approvals.map((a) => (
              <tr key={a.id} className="clickable" onClick={() => setSelected(a.id)}>
                <td>{a.title}</td>
                <td className="muted">{a.agent_name}</td>
                <td>
                  <span className="state-dot" style={{ background: RISK_TONE[a.risk_class] }} />
                  {a.risk_class}
                </td>
                <td className="muted">{new Date(a.created_at).toLocaleString()}</td>
                <td>
                  {a.status}
                  {a.was_modified && <span className="chip-tag">modified</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && <ApprovalDrawer id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

interface DecisionResult {
  status: 'approved' | 'rejected';
  executed: boolean;
  wasModified: boolean;
  error?: string;
}

function ApprovalDrawer({ id, onClose }: { id: string; onClose: () => void }): ReactNode {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<DecisionResult | null>(null);

  const detail = useQuery({
    queryKey: ['approval', id],
    queryFn: () => api.get<{ approval: ApprovalDetail }>(`/api/approvals/${id}`),
  });
  const approval = detail.data?.approval;

  const decide = useMutation({
    mutationFn: (body: {
      decision: 'approve' | 'reject';
      finalInput?: unknown;
      comment?: string;
    }) => api.post<DecisionResult>(`/api/approvals/${id}/decision`, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      void queryClient.invalidateQueries({ queryKey: ['approval', id] });
      void queryClient.invalidateQueries({ queryKey: ['control-center'] });
    },
    onSuccess: (result) => setOutcome(result),
  });

  function submit(decision: 'approve' | 'reject'): void {
    setInputError(null);

    let finalInput: unknown;
    if (decision === 'approve' && editing) {
      try {
        finalInput = JSON.parse(draft);
      } catch {
        // Refuse rather than send something the tool will reject halfway through.
        setInputError('That is not valid JSON. Fix it before approving.');
        return;
      }
    }

    decide.mutate({
      decision,
      ...(finalInput === undefined ? {} : { finalInput }),
      ...(comment.trim() ? { comment: comment.trim() } : {}),
    });
  }

  const pending = approval?.status === 'pending';
  const error = decide.error;

  return (
    <div className="drawer-backdrop" onClick={onClose} role="presentation">
      <aside className="drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal>
        <header className="drawer-header">
          <h2>{approval?.title ?? 'Approval'}</h2>
          <button type="button" className="link" onClick={onClose}>
            Close
          </button>
        </header>

        {detail.isLoading && <p className="muted">Loading…</p>}

        {approval && (
          <div className="drawer-body">
            <dl className="kv">
              <dt>Agent</dt>
              <dd>{approval.agent_name}</dd>
              <dt>Action</dt>
              <dd>
                <code>{approval.tool_key}</code>
              </dd>
              <dt>Risk</dt>
              <dd style={{ color: RISK_TONE[approval.risk_class] }}>{approval.risk_class}</dd>
              <dt>Requested</dt>
              <dd>{new Date(approval.created_at).toLocaleString()}</dd>
            </dl>

            <h3>Recommendation</h3>
            <p>{approval.recommendation.action}</p>
            <ul className="reasons">
              {(approval.recommendation.why ?? []).map((why, i) => (
                <li key={i}>{why}</li>
              ))}
            </ul>
            {approval.recommendation.confidence && (
              <p className="muted">Confidence: {approval.recommendation.confidence}</p>
            )}
            {approval.recommendation.data_sources && (
              <p className="muted">
                Data sources: {approval.recommendation.data_sources.join(', ')}
              </p>
            )}

            <h3>Supporting data</h3>
            <pre className="json">{JSON.stringify(approval.supporting_data, null, 2)}</pre>

            <h3>What would run</h3>
            <pre className="json">
              {JSON.stringify(approval.final_input ?? approval.proposed_input, null, 2)}
            </pre>

            {approval.status !== 'pending' && (
              <div className="alert">
                <strong>{approval.status === 'approved' ? 'Approved' : 'Rejected'}</strong>
                {approval.decided_by_name && ` by ${approval.decided_by_name}`}
                {approval.decided_at && ` on ${new Date(approval.decided_at).toLocaleString()}`}
                {approval.was_modified && ' — the input was modified before it was approved.'}
                <br />
                {approval.status === 'approved' &&
                  (approval.executed_at
                    ? `Executed at ${new Date(approval.executed_at).toLocaleString()}.`
                    : 'Not executed.')}
                {approval.comment && (
                  <>
                    <br />
                    <em>{approval.comment}</em>
                  </>
                )}
              </div>
            )}

            {pending && (
              <>
                <h3>Your decision</h3>

                <label className="field">
                  <span>Comment (optional)</span>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={2}
                    placeholder="Why you decided this way."
                  />
                </label>

                <label className="field checkbox">
                  <input
                    type="checkbox"
                    checked={editing}
                    onChange={(e) => {
                      setEditing(e.target.checked);
                      setDraft(JSON.stringify(approval.proposed_input, null, 2));
                      setInputError(null);
                    }}
                  />
                  <span>Change what runs before approving</span>
                </label>

                {editing && (
                  <label className="field">
                    <span>Modified input</span>
                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      rows={8}
                      className="mono"
                    />
                  </label>
                )}

                {inputError && <p className="error">{inputError}</p>}

                <div className="actions">
                  <button
                    type="button"
                    className="primary"
                    disabled={decide.isPending}
                    onClick={() => submit('approve')}
                  >
                    {editing ? 'Approve with changes' : 'Approve'}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    disabled={decide.isPending}
                    onClick={() => submit('reject')}
                  >
                    Reject
                  </button>
                </div>
              </>
            )}

            {/* Outside the `pending` block on purpose. A decision that was recorded but did not
                execute comes back as a 502, so the approval stops being pending and the decision
                controls unmount — taking the explanation with them, and leaving the owner with
                "Not executed" and no reason. This is the reason. */}
            {error && (
              <p className="error">
                {error instanceof ApiError && error.killSwitch
                  ? 'The platform is in READ-ONLY mode. Nothing was recorded.'
                  : (error as Error).message}
              </p>
            )}

            {outcome && (
              <div
                className={
                  outcome.executed || outcome.status === 'rejected' ? 'alert' : 'alert alert-warn'
                }
              >
                {outcome.status === 'rejected' && 'Rejected. Nothing ran.'}
                {outcome.status === 'approved' &&
                  (outcome.executed
                    ? `Approved and executed${outcome.wasModified ? ', with your changes' : ''}.`
                    : // Said plainly. The decision stands; the action did not happen.
                      `Approved${outcome.wasModified ? ' with your changes' : ''}, but the action did not run. ${outcome.error ?? ''}`)}
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
